/**
 * DataService.js
 * High-level data operations for Data-Next app
 * Abstracts FirebaseService with business logic
 */

import FirebaseService from "./FirebaseService";
import AgentAPIService from "./AgentAPIService";

class DataService {
  constructor() {
    this.firebaseService = new FirebaseService();
    this.agentAPI = new AgentAPIService();
  }

  // ==================== TEAMS ====================

  async getTeams() {
    return await this.firebaseService.getTeams();
  }

  async getTeam(teamId) {
    return await this.firebaseService.getTeam(teamId);
  }

  async createTeam(name, description = "") {
    return await this.firebaseService.createTeam({ name, description });
  }

  async updateTeam(teamId, updates) {
    return await this.firebaseService.updateTeam(teamId, updates);
  }

  async deleteTeam(teamId) {
    return await this.firebaseService.deleteTeam(teamId);
  }

  async addTeamMember(teamId, email, role = "reader") {
    const team = await this.getTeam(teamId);
    if (!team) throw new Error("Team not found");

    const lowerEmail = email.toLowerCase();
    const updates = {};

    if (role === "creator") {
      const creators = team.creators || [];
      if (!creators.includes(lowerEmail)) {
        updates.creators = [...creators, lowerEmail];
      }
    } else if (role === "reader") {
      const readers = team.readers || [];
      if (!readers.includes(lowerEmail)) {
        updates.readers = [...readers, lowerEmail];
      }
    }

    if (Object.keys(updates).length > 0) {
      await this.updateTeam(teamId, updates);
    }
  }

  async removeTeamMember(teamId, email) {
    const team = await this.getTeam(teamId);
    if (!team) throw new Error("Team not found");

    const lowerEmail = email.toLowerCase();
    const updates = {};

    if (team.creators && team.creators.includes(lowerEmail)) {
      updates.creators = team.creators.filter((e) => e !== lowerEmail);
    }

    if (team.readers && team.readers.includes(lowerEmail)) {
      updates.readers = team.readers.filter((e) => e !== lowerEmail);
    }

    if (Object.keys(updates).length > 0) {
      await this.updateTeam(teamId, updates);
    }
  }

  // ==================== CONTEXTS ====================

  async getContexts(teamId) {
    return await this.firebaseService.getContexts(teamId);
  }

  async getContext(teamId, contextId) {
    return await this.firebaseService.getContext(teamId, contextId);
  }

  async uploadContext(teamId, file, name, description = "", tags = []) {
    const user = this.firebaseService.getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error("File size exceeds 10MB limit");
    }

    // Generate context ID
    const contextId = `ctx_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}`;
    const ext = file.name.split(".").pop().toLowerCase();
    const storagePath = `data-next-teams/${user.uid}/contexts/${contextId}.${ext}`;

    // Determine context type
    const type = this.determineContextType(file.type);

    // For text files, read content directly
    let extractedText = "";
    let status = "processing";

    if (ext === "txt" || file.type === "text/plain") {
      try {
        extractedText = await file.text();
        status = "ready";
        console.log("Text file processed immediately");
      } catch (error) {
        console.error("Error reading text file:", error);
      }
    }

    // Upload to Storage
    const uploadResult = await this.firebaseService.uploadFile(
      file,
      storagePath,
      (progress) => {
        console.log(`Upload progress: ${progress}%`);
      }
    );

    // Create context document in Firestore
    const contextData = {
      contextId,
      name: name || file.name,
      type,
      description,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      storagePath: uploadResult.path,
      downloadURL: uploadResult.downloadURL,
      extractedText,
      metadata: {
        originalName: file.name,
        uploadedFrom: "web",
        extension: ext,
      },
      tags,
      status,
    };

    await this.firebaseService.createContext(teamId, contextData);

    // Trigger backend processing for non-text files
    if (status === "processing") {
      this.agentAPI
        .processDocument(
          teamId,
          contextId,
          uploadResult.downloadURL,
          file.type,
          ext
        )
        .catch((err) => console.error("Background processing error:", err));
    }

    return contextId;
  }

  determineContextType(mimeType) {
    if (mimeType.startsWith("image/")) return "screenshot";
    if (mimeType === "application/pdf" || mimeType.includes("document"))
      return "document";
    if (mimeType === "text/plain" || mimeType === "text/markdown")
      return "note";
    return "document";
  }

  async updateContext(teamId, contextId, updates) {
    return await this.firebaseService.updateContext(teamId, contextId, updates);
  }

  async deleteContext(teamId, contextId) {
    // Get context to find storage path
    const context = await this.getContext(teamId, contextId);
    if (!context) throw new Error("Context not found");

    // Delete from Storage
    if (context.storagePath) {
      try {
        await this.firebaseService.deleteFile(context.storagePath);
      } catch (error) {
        console.error("Error deleting file from storage:", error);
      }
    }

    // Delete from Firestore
    await this.firebaseService.deleteContext(teamId, contextId);
  }

  // ==================== AGENT EXECUTIONS ====================

  async getAgentExecutions(teamId, limitCount = 50) {
    return await this.firebaseService.getAgentExecutions(teamId, limitCount);
  }

  async getAgentExecution(teamId, executionId) {
    return await this.firebaseService.getAgentExecution(teamId, executionId);
  }

  async executeFlowchartAgent(contextIds, prompt, options, teamId) {
    // Call backend API
    const result = await this.agentAPI.executeFlowchartAgent({
      teamId,
      contextIds,
      prompt,
      options,
    });

    return result;
  }

  async saveAgentExecution(teamId, executionData) {
    return await this.firebaseService.createAgentExecution(
      teamId,
      executionData
    );
  }

  async updateAgentExecution(teamId, executionId, updates) {
    return await this.firebaseService.updateAgentExecution(
      teamId,
      executionId,
      updates
    );
  }

  async deleteAgentExecution(teamId, executionId) {
    return await this.firebaseService.deleteAgentExecution(teamId, executionId);
  }

  async saveExecutionVersion(
    teamId,
    executionId,
    newCode,
    changeDescription = ""
  ) {
    const execution = await this.getAgentExecution(teamId, executionId);
    if (!execution) throw new Error("Execution not found");

    const user = this.firebaseService.getCurrentUser();
    const versions = execution.versions || [];

    const newVersion = {
      version: versions.length + 1,
      mermaidCode: newCode,
      editedBy: user.uid,
      editedByEmail: user.email,
      editedAt: Date.now(),
      changeDescription,
    };

    await this.updateAgentExecution(teamId, executionId, {
      result: {
        ...execution.result,
        mermaidCode: newCode,
      },
      versions: [...versions, newVersion],
    });
  }
}

export default DataService;
